import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiGet } from '../services/api';

const CountryContext = createContext(null);

// Static fallback — used if the backend /location/countries.php fails or returns empty
const STATIC_COUNTRIES = [
  { id: 1,  country_code: 'IN', country_name: 'India',                currency_code: 'INR', currency_symbol: '₹' },
  { id: 2,  country_code: 'US', country_name: 'United States',        currency_code: 'USD', currency_symbol: '$' },
  { id: 3,  country_code: 'GB', country_name: 'United Kingdom',       currency_code: 'GBP', currency_symbol: '£' },
  { id: 4,  country_code: 'CA', country_name: 'Canada',               currency_code: 'CAD', currency_symbol: 'C$' },
  { id: 5,  country_code: 'AU', country_name: 'Australia',            currency_code: 'AUD', currency_symbol: 'A$' },
  { id: 6,  country_code: 'AE', country_name: 'United Arab Emirates', currency_code: 'AED', currency_symbol: 'د.إ' },
  { id: 7,  country_code: 'SG', country_name: 'Singapore',            currency_code: 'SGD', currency_symbol: 'S$' },
  { id: 8,  country_code: 'NP', country_name: 'Nepal',                currency_code: 'NPR', currency_symbol: 'रू' },
  { id: 9,  country_code: 'DE', country_name: 'Germany',              currency_code: 'EUR', currency_symbol: '€' },
  { id: 10, country_code: 'FR', country_name: 'France',               currency_code: 'EUR', currency_symbol: '€' },
  { id: 11, country_code: 'IT', country_name: 'Italy',                currency_code: 'EUR', currency_symbol: '€' },
  { id: 12, country_code: 'ES', country_name: 'Spain',                currency_code: 'EUR', currency_symbol: '€' },
  { id: 13, country_code: 'NL', country_name: 'Netherlands',          currency_code: 'EUR', currency_symbol: '€' },
];

// Static exchange-rate fallbacks (INR base) for when currency/convert.php is unavailable
const FALLBACK_RATES = {
  INR: 1.0,
  USD: 0.012,
  GBP: 0.0094,
  EUR: 0.011,
  CAD: 0.016,
  AUD: 0.018,
  AED: 0.044,
  SGD: 0.016,
  NPR: 1.6,
};

export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
};

export const CountryProvider = ({ children }) => {
  const [selectedCountry, setSelectedCountry] = useState({
    country_code: 'IN',
    country_name: 'India',
    currency_code: 'INR',
    currency_symbol: '₹',
    exchange_rate: 1.0
  });
  const [countriesList, setCountriesList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Helper: get fallback rate for a currency code
  const getFallbackRate = (currencyCode) => FALLBACK_RATES[currencyCode] || 1.0;

  // Helper: fetch live exchange rate with fallback
  const fetchRate = async (currencyCode) => {
    if (!currencyCode || currencyCode === 'INR') return 1.0;
    try {
      const conversion = await apiGet(`/currency/convert.php?amount_inr=1&target_currency=${currencyCode}`);
      if (conversion && conversion.exchange_rate) return parseFloat(conversion.exchange_rate);
    } catch (e) {
      // silent — use static fallback
    }
    return getFallbackRate(currencyCode);
  };

  // Initialize: Fetch countries list & detect country
  useEffect(() => {
    async function initCountry() {
      try {
        setIsLoading(true);

        // 1. Fetch countries list — fall back to static list if API fails or returns empty
        let fetchedCountries = [];
        try {
          const result = await apiGet('/location/countries.php');
          if (result && result.length > 0) {
            fetchedCountries = result;
          }
        } catch (e) {
          console.warn('Countries API unavailable, using static fallback.', e);
        }
        const countriesData = fetchedCountries.length > 0 ? fetchedCountries : STATIC_COUNTRIES;
        setCountriesList(countriesData);

        // 2. Check localStorage for override
        const cached = localStorage.getItem('selectedCountry');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            const rate = await fetchRate(parsed.currency_code);
            setSelectedCountry({ ...parsed, exchange_rate: rate });
            return; // done — user had a saved preference
          } catch (e) {
            localStorage.removeItem('selectedCountry');
          }
        }

        // 3. Auto-detect country from backend (Cloudflare CF-IPCountry header)
        try {
          const detected = await apiGet('/location/detect-country.php');
          if (detected && detected.country_code) {
            const rate = await fetchRate(detected.currency_code);
            const countryObj = {
              country_code: detected.country_code,
              country_name: detected.country_name,
              currency_code: detected.currency_code,
              currency_symbol: detected.currency_symbol,
              exchange_rate: rate
            };
            setSelectedCountry(countryObj);
            localStorage.setItem('selectedCountry', JSON.stringify({
              country_code: detected.country_code,
              country_name: detected.country_name,
              currency_code: detected.currency_code,
              currency_symbol: detected.currency_symbol
            }));
          }
        } catch (detErr) {
          console.warn('Auto-detect country failed, using default India.', detErr);
          // Default India is already set in initial state — no action needed
        }
      } catch (err) {
        console.error('Country Context initialization error:', err);
        // Ensure static fallback is always set even if everything fails
        if (countriesList.length === 0) setCountriesList(STATIC_COUNTRIES);
      } finally {
        setIsLoading(false);
      }
    }
    initCountry();
  }, []);

  // Change manual country override
  const changeCountry = async (countryCode) => {
    try {
      setIsLoading(true);
      // Search in API list first, then fall back to static list
      const allSources = [...countriesList, ...STATIC_COUNTRIES];
      const match = allSources.find(c => c.country_code === countryCode);
      if (match) {
        const rate = await fetchRate(match.currency_code);
        const countryObj = {
          country_code: match.country_code,
          country_name: match.country_name,
          currency_code: match.currency_code,
          currency_symbol: match.currency_symbol,
          exchange_rate: rate
        };
        setSelectedCountry(countryObj);
        localStorage.setItem('selectedCountry', JSON.stringify({
          country_code: match.country_code,
          country_name: match.country_name,
          currency_code: match.currency_code,
          currency_symbol: match.currency_symbol
        }));
      }
    } catch (err) {
      console.error('Failed to change manual country:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to convert price (numeric) with optional course object / charges
  const convertPrice = (inrAmount, courseOrOpts = null) => {
    const amt = parseFloat(inrAmount);
    if (isNaN(amt)) return 0;

    const isDomestic = selectedCountry.country_code === 'IN' || selectedCountry.currency_code === 'INR';
    const rate = selectedCountry.exchange_rate || 1.0;

    let domPct = 0;
    let intlPct = 0;
    if (courseOrOpts && typeof courseOrOpts === 'object') {
      domPct = parseFloat(courseOrOpts.domestic_charges_pct) || 0;
      intlPct = parseFloat(courseOrOpts.international_charges_pct) || 0;
    } else if (typeof courseOrOpts === 'number') {
      domPct = courseOrOpts;
    }

    if (isDomestic) {
      // Domestic: Base Fee + Domestic Charges %
      const baseWithCharge = domPct > 0 ? amt + (amt * domPct / 100) : amt;
      return roundToNiceNumber(baseWithCharge, 'INR');
    } else {
      // International: Base Fee -> Existing Currency Conversion -> Converted Fee -> Apply International Charges %
      const convertedBase = roundToNiceNumber(amt * rate, selectedCountry.currency_code);
      const finalFee = intlPct > 0 ? convertedBase + (convertedBase * intlPct / 100) : convertedBase;
      return roundToNiceNumber(finalFee, selectedCountry.currency_code);
    }
  };

  // Round values to look neat (no decimals for large numbers unless a decimal portion exists)
  const roundToNiceNumber = (val, currency) => {
    if (val % 1 !== 0) {
      return parseFloat(val.toFixed(2));
    }
    return Math.round(val);
  };

  // Helper to format price (string) with optional course object / charges
  const formatPrice = (inrAmount, courseOrOpts = null) => {
    const amt = parseFloat(inrAmount);
    if (isNaN(amt) || amt <= 0) return "Fee will be updated soon";
    const converted = convertPrice(amt, courseOrOpts);
    const symbol = selectedCountry.currency_symbol || '';
    const code = selectedCountry.currency_code;
    const formatted = converted.toLocaleString(undefined, {
      minimumFractionDigits: (converted % 1 === 0) ? 0 : 2,
      maximumFractionDigits: 2
    });
    return `${symbol}${formatted} ${code}`;
  };

  const getShortCode = (countryCode) => {
    const countryShortCodes = {
      IN: 'IND',
      US: 'USA',
      GB: 'UK',
      CA: 'CAN',
      AU: 'AUS',
      AE: 'UAE',
      SG: 'SGP',
      NP: 'NEP',
      DE: 'GER',
      FR: 'FRA',
      IT: 'ITA',
      ES: 'ESP',
      NL: 'NLD'
    };
    return countryShortCodes[countryCode] || countryCode;
  };

  return (
    <CountryContext.Provider value={{
      selectedCountry,
      countriesList,
      isLoading,
      changeCountry,
      convertPrice,
      formatPrice,
      getShortCode
    }}>
      {children}
    </CountryContext.Provider>
  );
};
